import React, { useMemo } from 'react';
import type { AcquisitionPeriod, VacationBalance, VacationRequest } from '@/features/Vacations/models/vacation.types';
import { buildVacationStatementRows, calculateStatementBalance } from '@/features/Vacations/utils/vacationStatementRows';

interface VacationHistoryEntry {
    date: string;
    description: string;
    credit?: number;
    debit?: number;
}

interface VacationStatementProps {
    vacationHistory?: VacationHistoryEntry[] | null;
    balance?: number;
    balanceObj?: VacationBalance | null;
    periods?: AcquisitionPeriod[];
    vacationPeriods?: Array<{ startDate?: string; endDate?: string; days?: number; observations?: string }>;
    requests?: VacationRequest[];
}

const formatNumber = (num: number) => new Intl.NumberFormat('pt-BR').format(num);

const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
};

const VacationStatement: React.FC<VacationStatementProps> = ({
    balance = 0,
    balanceObj,
    periods = [],
    vacationPeriods = [],
    requests = [],
}) => {
    // Usar nova lógica de razão se temos balance + requests, senão fallback legado
    const rows: VacationHistoryEntry[] = useMemo(() => {
        if (balanceObj && requests.length > 0) {
            // Nova lógica: razão item a item
            const statementRows = buildVacationStatementRows(balanceObj, vacationPeriods, requests);
            return statementRows.map((row) => ({
                date: row.date,
                description: row.description,
                credit: row.credit,
                debit: row.debit,
            }));
        }

        // Fallback legado: razão agregada por período aquisitivo
        const legacyRows: VacationHistoryEntry[] = [];

        for (const period of periods) {
            if (period.status === 'Accruing') continue;

            // Linha de crédito do período
            const startYear = period.startDate.split('-')[0];
            const endYear = period.endDate.split('-')[0];
            const periodLabel = startYear === endYear
                ? `${startYear}`
                : `${startYear}–${endYear}`;

            legacyRows.push({
                date: period.startDate,
                description: `Crédito Período Aquisitivo (${periodLabel})`,
                credit: period.entitledDays,
            });

            // Débitos do período (se maiores que zero)
            if (period.takenDays > 0) {
                legacyRows.push({
                    date: period.startDate,
                    description: 'Férias Gozadas',
                    debit: period.takenDays,
                });
            }
            if (period.soldDays > 0) {
                legacyRows.push({
                    date: period.startDate,
                    description: 'Abono Pecuniário',
                    debit: period.soldDays,
                });
            }
            if (period.scheduledDays > 0) {
                legacyRows.push({
                    date: period.startDate,
                    description: 'Férias Agendadas',
                    debit: period.scheduledDays,
                });
            }
        }

        // Adicionar linhas do histórico lançado (vacationPeriods) como detalhe informativo
        for (const vp of vacationPeriods) {
            if (!vp.startDate || !vp.endDate) continue;
            legacyRows.push({
                date: vp.startDate,
                description: `Histórico: ${vp.observations || 'Férias'}`,
                debit: vp.days || 0,
            });
        }

        // Ordenar cronologicamente
        legacyRows.sort((a, b) => a.date.localeCompare(b.date));
        return legacyRows;
    }, [balanceObj, requests, periods, vacationPeriods]);

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">Extrato de Férias</h3>
                <p className="mt-1 text-sm text-gray-500">
                    Histórico de concessões e usos de dias de férias.
                </p>
            </div>
            <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Data</th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Descrição</th>
                            <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Crédito</th>
                            <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Débito</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {rows.length > 0 ? (
                            rows.map((entry, index) => (
                                <tr key={index}>
                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{formatDate(entry.date)}</td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{entry.description}</td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-green-600">{entry.credit ? formatNumber(entry.credit) : '—'}</td>
                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-red-600">{entry.debit ? formatNumber(entry.debit) : '—'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={4} className="text-center py-8 text-gray-500">
                                    Nenhum registro de férias encontrado.
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot className="bg-gray-50">
                        <tr>
                            <td colSpan={2} className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Saldo Final</td>
                            <td colSpan={2} className="px-3 py-3.5 text-right text-lg font-bold text-gray-900">{formatNumber(balance || 0)} dias</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default VacationStatement;
